<template>
  <Loader v-if="isLoading" />
  <SurveyPageWrapper v-else>
    <template #logo>
      <Logo :src="logoSrc" />
    </template>
    <template #form>
      <transition name="fade">
        <Loader v-if="isViewLoading" is-nested />
      </transition>
      <ProgressBar
        v-if="progressPercentage > 0"
        :percentage="progressPercentage"
      />
      <DynamicForm ref="form" :page-fields="pageFields" @submit="submitHandler">
        <Button v-if="showSubmitButton" block @click="submitHandler">{{
          nextButtonText || $t('nextViewButtonText')
        }}</Button>
      </DynamicForm>
    </template>
    <template #footer>
      <SurveyPageFooter />
    </template>
  </SurveyPageWrapper>
</template>

<script lang="ts">
import { Component, Vue, Mixins, Watch } from 'vue-property-decorator';
import { SurveysService } from '@/services/surveys.service';
import Loader from '@/components/Loader.vue';
import DynamicForm from '@/components/DynamicForm.vue';
import GroupingElement from '@/components/widgets/GroupingElement.vue';
import SurveyPageWrapper from '@/components/SurveyPageWrapper.vue';
import Logo from '@/components/Logo.vue';
import { Survey } from '@/models/survey';
import isMobile from '@/utils/isMobile';
import { DEVICE } from '@/models/enums/device.enum';
import { SurveyElementsUnion } from '@/models/survey-elements-union';
import { SurveyPayloadElement } from '@/models/survey-payload-element';
import { SurveySession } from '@/models/survey-session';
import { ANALYTICS_ACTIONS } from '@/models/enums/analytics-actions.enum';
import SessionManager from '@/helpers/session-manager';
import PageFieldsManager from '@/helpers/page-fields-manager';
import AppConfigMixin from '@/mixins/appConfigMixin';
import ErrorHandler from '@/helpers/error-handler';
import SurveyPageFooter from '@/components/SurveyPageFooter.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import { COMPONENT_TYPES } from '@/models/enums/component-types.enum';
import { BUTTON_ELEMENT_ACTION_TYPES } from '@/models/enums/button-element-action-types.enum';
import { LocaleMessageObject } from 'vue-i18n';
import getUserOs from '@/helpers/detect-os';
import mixpanel from 'mixpanel-browser';
import { EventBus } from '@/event-bus';
import { WIDGET_EVENTS } from '@/const/widget-events';

@Component({
  components: {
    SurveyPageFooter,
    ProgressBar,
    Loader,
    DynamicForm,
    SurveyPageWrapper,
    Logo,
    GroupingElement,
    Button: () => import('@/components/Button.vue'),
  },
})
export default class SurveyPageView extends Mixins(AppConfigMixin) {
  $refs!: Vue['$refs'] & {
    form: InstanceType<typeof DynamicForm>;
  };
  private pageFields: SurveyElementsUnion[] = [];
  private showSubmitButton: boolean = false;
  private isLoading: boolean = false;
  private isViewLoading: boolean = false;
  private surveySession: SurveySession | null = null;
  private surveySessionManager: SessionManager | null = null;
  private pageFieldsManager: PageFieldsManager | null = null;
  private isSessionEnabled: boolean = false;
  private nextButtonText: null | string = null;
  private totalSteps: number = 9;
  private currentStep: number = 6;

  private get progressPercentage(): number {
    const progress = this.$store.state.progress;
    const currentStep = Number(progress?.currentStep ?? 0);
    const totalSteps = Number(progress?.totalSteps ?? 0);

    if (
      !progress ||
      isNaN(currentStep) ||
      isNaN(totalSteps) ||
      totalSteps <= 0
    ) {
      return 0;
    }

    const isTerminalView = !this.$store.state.isNextView;
    const shouldReduce =
      currentStep === totalSteps &&
      (!isTerminalView || this.hasGoogleMapsRedirect);
    const adjustedStep = shouldReduce ? currentStep - 0.5 : currentStep;

    const ratio = adjustedStep / totalSteps;

    if (!isFinite(ratio) || ratio < 0) {
      return 0;
    }

    return Math.min(Math.max(ratio * 100, 0), 100);
  }

  private get hasGoogleMapsRedirect(): boolean {
    return this.pageFields.some(
      item =>
        item.elementType === COMPONENT_TYPES.BUTTON &&
        'action' in item &&
        (item as { action: number }).action ===
          BUTTON_ELEMENT_ACTION_TYPES.GO_TO_MAPS
    );
  }

  private get logoSrc(): string | null {
    return this.$store.getters.logo;
  }

  private async created() {
    this.isLoading = true;

    if (!this.checkIfSessionSlugMatches) {
      this.redirectToSurvey();
    }

    await this.setConfig();
    if (this.$store.getters.isVox) {
      document?.documentElement.style.setProperty(
        '--custom-font-name',
        'Uniform'
      );
    } else {
      document?.documentElement.style.setProperty(
        '--custom-font-name',
        'Montserrat'
      );
    }
    await Promise.all([this.setFlowConfig(), this.setTranslations()]);
    this.setDefaultColors();
    this.isLoading = false;
    await this.sendSurveyAnalytics(ANALYTICS_ACTIONS.VIEW, this.surveyId!);
  }

  mounted() {
    document.querySelector('body')!.style.overflow = isMobile()
      ? 'hidden'
      : 'auto';
  }

  private async setTranslations() {
    this.$i18n.locale = this.$store.getters.defaultLanguage.code;
    document.title = this.$t('metaTitle').toString();

    const translations = await SurveysService.getTranslations(
      this.$i18n.locale
    );

    document.documentElement.lang = this.$i18n.locale;

    this.$i18n.mergeLocaleMessage(this.$i18n.locale, {
      ...translations,
    } as LocaleMessageObject);
  }

  private hexToRgb(hex: string) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(
          result[3],
          16
        )}`
      : null;
  }

  private setDefaultColors() {
    const colors = this.$store.getters.colorsTheme;

    document.body.style.setProperty(
      '--core-color',
      this.hexToRgb(colors.primary)
    );
    document.body.style.setProperty('--bg-color', colors.bg);
  }

  private async submitHandler() {
    const isValid = await this.$refs.form.validate();
    if (!isValid) {
      return;
    }
    const pageFieldsPayload: SurveyPayloadElement[] =
      this.pageFieldsManager!.getPageFieldsPayload();

    if (!this.surveyId) return; //handle error
    try {
      this.$store.dispatch('setIsAnswerInProgress', true);
      this.isViewLoading = true;
      await SurveysService.sendAnswer(pageFieldsPayload);
      this.$store.dispatch('setIsAnswerInProgress', false);

      await this.setPageFields(() =>
        SurveysService.getNextStep(this.surveyId!)
      );
      if (this.isSessionEnabled && !this.surveySessionManager?.getSession()) {
        this.surveySessionManager?.createSession(
          this.surveyId,
          this.$store.state.config.slug,
          this.$store.state.defaultFacility.id,
          this.$route.params
        );
      }
      this.$nextTick(() => {
        this.isViewLoading = false;
      });
    } catch (error) {
      this.isViewLoading = false;
      this.$store.dispatch('setIsAnswerInProgress', false);

      ErrorHandler.handle(error);
    }
  }

  private checkIfSessionSlugMatches() {
    const { slug } = this.$store.state.config;
    const { slug: sessionSlug } = this.surveySession!;
    return slug === sessionSlug;
  }

  private deleteSession() {
    SessionManager.deleteSession();
    this.surveySession = null;
  }

  async setPageFields(getPageFieldsCallback: () => Promise<Survey>) {
    const {
      id,
      showSubmitButton,
      isNextView,
      activeViewName,
      activeBranchIdentifier,
      activeView: { elements, nextViewElement },
      progress,
    } = await getPageFieldsCallback();

    this.$store.dispatch('setSurveyId', id);
    this.$store.dispatch('setActiveViewName', activeViewName);
    this.$store.dispatch('setProgress', progress);
    this.$store.dispatch('setActiveBranchIdentifier', activeBranchIdentifier);
    this.$store.dispatch('setIsNextView', isNextView);
    this.showSubmitButton = showSubmitButton;
    this.pageFieldsManager = new PageFieldsManager(
      elements,
      isNextView,
      showSubmitButton,
      id
    );
    this.pageFields = this.pageFieldsManager.getPageFields();

    this.nextButtonText = nextViewElement ? nextViewElement.text : null;

    if (this.pageFieldsManager.isSessionFinished()) {
      this.deleteSession();
    }
  }

  private redirectToSurvey() {
    this.deleteSession();

    this.$router.push({
      name: 'survey',
      params: { slug: this.$route.params.slug },
    });
  }

  private async setFlowConfig() {
    this.surveySessionManager = new SessionManager();
    this.surveySession = this.surveySessionManager.validateAndGetSessionData(
      this.$route.params
    );

    await this.setDefaultFacility(this.redirectToSurvey);

    if (this.surveySession) {
      await this.setPageFields(() =>
        SurveysService.getSurveysSession(this.surveySession!.sessionId)
      );
      return;
    }
    await this.setPageFields(() =>
      SurveysService.getSurveys({
        os: getUserOs(),
        medium: isMobile() ? DEVICE.MOBILE : DEVICE.DESKTOP,
        origin_link: this.$store.state.originLink,
        origin_gmb_address: this.$store.state.defaultFacility?.gmbAddressId,
      })
    );

    const session_id = this.$store.state.surveyId;
    const currentDistinctId = mixpanel.get_distinct_id();
    if (currentDistinctId !== session_id) {
      mixpanel.identify(session_id);
    }
  }
}
</script>
<style>
/* Transition styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
</style>
